// Small custom dropdown that the triage filter row uses for the
// Workflow filter (and any other place a native <select> gets too tall
// to navigate comfortably). Browser-native dropdowns can't be styled
// for height + can't add a search field — this component supplies both.
// (DEV-16180.)
//
// Surface kept intentionally small:
//   - controlled `value` + `onChange`
//   - searchable when `searchable={true}` (default for >8 options)
//   - keyboard support: ↑↓ + Enter; Escape closes
//   - click-outside closes
//
// Not a kit replacement for ALL <select>s — only places where the
// option count is unbounded (workflows, agents, etc.). Short, static
// lists (status, trigger) keep the native control since it gives free
// platform integration (typeahead, mobile native picker, accessibility).
import { useEffect, useRef, useState, type CSSProperties } from 'react';

export interface ICompactSelectOption {
  value: string;
  label: string;
  /** Optional secondary line shown beneath the label. */
  description?: string;
}

export function CompactSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyOptionLabel,
  disabled,
  searchable,
  ariaLabel,
  width = 220,
  maxHeight = 280,
}: {
  value: string;
  onChange: (next: string) => void;
  options: readonly ICompactSelectOption[];
  placeholder?: string;
  /** Label for the "clear selection" pseudo-option (value: ''). Omit to hide. */
  emptyOptionLabel?: string;
  disabled?: boolean;
  /** Defaults to `true` when there are >8 options. */
  searchable?: boolean;
  ariaLabel?: string;
  /** Trigger + panel width. A number is px; pass `'100%'` to fill the parent. */
  width?: number | string;
  /** Max height of the open dropdown panel (px). Items beyond scroll. */
  maxHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const shouldShowSearch = searchable ?? options.length > 8;
  const showEmpty = emptyOptionLabel !== undefined;

  // Click outside closes the dropdown. Mounted only while open so the
  // listener doesn't run unnecessarily on every dropdown on the page.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus the search input as soon as the dropdown opens, so users can
  // start typing immediately. When unsearchable, focus a no-op so
  // keyboard navigation still works without the user having to click an
  // option first.
  useEffect(() => {
    if (open && shouldShowSearch) {
      searchInputRef.current?.focus();
    }
    if (open) setActiveIndex(0);
    if (!open) setQuery('');
  }, [open, shouldShowSearch]);

  const filtered = query.trim()
    ? options.filter((o) => {
        const q = query.toLowerCase();
        return o.label.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q);
      })
    : options;
  const visibleOptions: ICompactSelectOption[] = showEmpty
    ? [{ value: '', label: emptyOptionLabel ?? '' }, ...filtered]
    : [...filtered];

  const selectedLabel = options.find((o) => o.value === value)?.label;
  const triggerLabel = selectedLabel ?? (showEmpty ? emptyOptionLabel : null) ?? placeholder;

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={triggerStyle(disabled)}
      >
        <span style={triggerLabelStyle}>{triggerLabel}</span>
        <span aria-hidden="true" style={caretStyle}>
          ▾
        </span>
      </button>
      {open ? (
        <div role="listbox" style={panelStyle(width, maxHeight)}>
          {shouldShowSearch ? (
            <div style={searchRowStyle}>
              <input
                ref={searchInputRef}
                type="text"
                className="input"
                style={searchInputStyle}
                placeholder="Search…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActiveIndex((i) => Math.min(visibleOptions.length - 1, i + 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActiveIndex((i) => Math.max(0, i - 1));
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    const o = visibleOptions[activeIndex];
                    if (o) choose(o.value);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setOpen(false);
                  }
                }}
              />
            </div>
          ) : null}
          <div style={listStyle}>
            {visibleOptions.length === 0 ? (
              <div style={emptyMessageStyle}>No matches</div>
            ) : (
              visibleOptions.map((o, i) => (
                <button
                  key={o.value || '__none__'}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(o.value)}
                  style={optionStyle(i === activeIndex, o.value === value)}
                >
                  <span style={optionLabelStyle}>{o.label}</span>
                  {o.description ? (
                    <span style={optionDescriptionStyle}>{o.description}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const triggerStyle = (disabled?: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
  width: '100%',
  height: 30,
  padding: '0 var(--sp-2)',
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-xs)',
  color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 'var(--fs-sm)',
  textAlign: 'left',
});

const triggerLabelStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const caretStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--text-secondary)',
};

const panelStyle = (width: number | string, maxHeight: number): CSSProperties => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 50,
  marginTop: 4,
  width,
  maxHeight,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
  overflow: 'hidden',
});

const searchRowStyle: CSSProperties = {
  padding: 'var(--sp-1)',
  borderBottom: '1px solid var(--border-subtle)',
};

const searchInputStyle: CSSProperties = {
  width: '100%',
  height: 26,
  fontSize: 'var(--fs-xs)',
};

const listStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 'var(--sp-1)',
};

const optionStyle = (active: boolean, selected: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
  padding: '4px 8px',
  background: active ? 'var(--surface-2)' : 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-xs)',
  color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
  fontWeight: selected ? 'var(--fw-med)' : 'normal',
  fontSize: 'var(--fs-xs)',
  textAlign: 'left',
  cursor: 'pointer',
});

const optionLabelStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const optionDescriptionStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--text-muted)',
};

const emptyMessageStyle: CSSProperties = {
  padding: 'var(--sp-2)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-muted)',
  textAlign: 'center',
};
